<template>
  <div></div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { createWatermark, removeWatermark } from '@/utils/watermark'
import type { RootState } from '@/store'

interface WatermarkData {
  userIP: string | null;
  cleanup: (() => void) | null;
}

export default defineComponent({
  name: 'Watermark',
  data(): WatermarkData {
    return {
      userIP: null,
      cleanup: null
    }
  },
  computed: {
    user(): RootState['user'] {
      return (this.$store.state as RootState).user
    },
    isLoggedIn(): boolean {
      return (this.$store.state as RootState).isLoggedIn
    }
  },
  mounted() {
    // 组件挂载时创建水印
    if (this.isLoggedIn && this.user?.username) {
      this.updateWatermark()
    } else {
      // 未登录时获取IP并显示水印
      this.fetchIPAndUpdateWatermark()
    }
  },
  watch: {
    // 监听用户登录状态和用户信息变化
    isLoggedIn: {
      handler(newVal: boolean) {
        if (newVal && this.user?.username) {
          this.updateWatermark()
        } else {
          // 未登录时获取IP并显示水印
          this.fetchIPAndUpdateWatermark()
        }
      },
      immediate: true
    },
    'user.username': {
      handler() {
        if (this.isLoggedIn && this.user?.username) {
          this.updateWatermark()
        }
      },
      immediate: true
    }
  },
  methods: {
    /**
     * 获取用户IP地址
     */
    async fetchIP(): Promise<string | null> {
      try {
        // axios 拦截器对于外部 API 会返回 response.data，所以直接使用 response
        const response = (await this.$http.get('https://ip.yuntao.xyz/')) as { ip?: string } | undefined
        if (response && response.ip) {
          return response.ip
        }
        return null
      } catch (error) {
        console.error('获取IP地址失败:', error)
        return null
      }
    },
    /**
     * 获取IP并更新水印
     */
    async fetchIPAndUpdateWatermark(): Promise<void> {
      if (!this.userIP) {
        this.userIP = await this.fetchIP()
      }
      this.updateWatermark()
    },
    /**
     * 更新水印
     */
    updateWatermark(): void {
      // 先移除旧的水印
      if (this.cleanup) {
        this.cleanup()
        this.cleanup = null
      }
      removeWatermark()

      // 超管用户不显示水印
      if (this.$isSuperAdmin()) {
        return
      }
      
      let watermarkText = ''
      
      if (this.isLoggedIn && this.user?.username) {
        // 已登录：显示用户名
        watermarkText = this.user.username
      } else {
        // 未登录：显示IP地址
        watermarkText = this.userIP || '未登录'
      }

      if (!watermarkText) {
        return
      }
      
      // 创建新水印
      this.cleanup = createWatermark({
        text: watermarkText,
        font: '16px Arial',
        fillStyle: 'rgba(0, 0, 0, 0.08)', // 可以调整透明度，0.04表示4%不透明度
        width: 200,
        height: 150,
        rotate: -20, // 旋转角度
        zIndex: 9999
      })
    }
  },
  beforeDestroy() {
    // 组件销毁时清理水印
    if (this.cleanup) {
      this.cleanup()
    }
    removeWatermark()
  }
})
</script>

<style scoped>
/* 组件本身不需要样式，水印是通过工具函数动态创建的 */
</style>
